/* ============================================================
   Cognito user-pool API — direct HTTPS calls to the public cognito-idp
   endpoint (AWS JSON 1.1 wire protocol), no SDK and no polyfills.

   This is what powers the *native* email/password screens: sign-up, email
   verification, sign-in, and password reset all happen inside the app, so the
   user never sees a hosted web page. Social sign-in can't work this way —
   federating Google/Apple/Facebook through a user pool is only possible via
   /oauth2/authorize in a browser — so that path stays in AuthContext, with the
   browser round trip supplied by the platform's OAuthAdapter (it jumps straight
   to the provider, skipping Cognito's own page; see signInWithProvider).

   Why raw fetch instead of amazon-cognito-identity-js / Amplify: those bring
   SRP, which on React Native needs crypto polyfills (get-random-values, buffer)
   and the native-build friction that comes with them. USER_PASSWORD_AUTH sends
   the password to Cognito over TLS instead of proving it via SRP — an accepted
   AWS flow, and the tradeoff we take for a zero-dependency auth layer. It
   requires ALLOW_USER_PASSWORD_AUTH on the app client (see infra/cognito.tf).

   None of these calls are authenticated: the app client is public and has no
   secret, so no request signing (SigV4) is involved.

   A factory rather than module-level functions so the pool it talks to comes
   from config (ClientCoreProvider) instead of an env var read at import time —
   the one thing that differs between front ends. The pure helpers at the
   bottom (password rules, email check) need no pool and stay plain exports.
   ============================================================ */
import type { CognitoConfig } from './config';

const TARGET_PREFIX = 'AWSCognitoIdentityProviderService';

/** A failed Cognito call. `code` is the raw AWS exception name (e.g.
   `UserNotConfirmedException`) so callers can branch on it; `message` is
   already user-facing copy. */
export class CognitoError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'CognitoError';
    this.code = code;
  }
}

/* User-facing copy per AWS exception. Calm and specific — never blame the user,
   and never leak whether an account exists (the pool also has
   prevent_user_existence_errors = ENABLED, which folds UserNotFound into
   NotAuthorized server-side). */
function messageFor(code: string, serverMessage: string): string {
  switch (code) {
    case 'NotAuthorizedException':
      // also returned for a revoked/expired refresh token — the sign-in copy is
      // the common case and the refresh path never surfaces this to the user
      return "That email and password don't match.";
    case 'UserNotFoundException':
      return "That email and password don't match.";
    case 'UserNotConfirmedException':
      return 'This email still needs to be verified.';
    case 'UsernameExistsException':
      return 'There’s already an account with this email.';
    case 'InvalidPasswordException':
      return 'That password doesn’t meet the requirements below.';
    case 'InvalidParameterException':
      return serverMessage || 'Something in that form wasn’t accepted.';
    case 'CodeMismatchException':
      return 'That code doesn’t match. Check the digits and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired — send yourself a new one.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Wait a moment, then try again.';
    case 'CodeDeliveryFailureException':
      return 'We couldn’t send that email. Try again in a moment.';
    case 'PasswordResetRequiredException':
      return 'This account needs a new password — use “Forgot password”.';
    case 'NetworkError':
      return 'Can’t reach the server — check your connection.';
    default:
      return serverMessage || 'Something went wrong. Try again.';
  }
}

async function callOp<T>(endpoint: string, op: string, body: Record<string, unknown>): Promise<T> {
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `${TARGET_PREFIX}.${op}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new CognitoError('NetworkError', messageFor('NetworkError', ''));
  }

  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;

  if (!res.ok) {
    // AWS returns the exception name in `__type`, sometimes namespaced as
    // "com.amazonaws...#NotAuthorizedException".
    const raw = typeof json.__type === 'string' ? json.__type : '';
    const code = raw.split('#').pop() || 'UnknownError';
    const serverMessage = typeof json.message === 'string' ? json.message : '';
    throw new CognitoError(code, messageFor(code, serverMessage));
  }

  return json as T;
}

/** Tokens as this module hands them back — same shape the token store keeps,
   minus the storage-only `origin` field. */
export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  /** absent on a refresh — Cognito only issues one at initial sign-in */
  refreshToken: string | null;
  /** epoch ms */
  expiresAt: number;
}

interface AuthenticationResult {
  AccessToken: string;
  IdToken: string;
  RefreshToken?: string;
  ExpiresIn: number;
}

interface InitiateAuthResponse {
  AuthenticationResult?: AuthenticationResult;
  ChallengeName?: string;
}

function toTokens(r: AuthenticationResult): CognitoTokens {
  return {
    accessToken: r.AccessToken,
    idToken: r.IdToken,
    refreshToken: r.RefreshToken ?? null,
    expiresAt: Date.now() + r.ExpiresIn * 1000,
  };
}

export interface SignUpResult {
  /** true when no email verification is needed (pool auto-confirms) */
  userConfirmed: boolean;
  /** masked address the code went to, e.g. "j***@e***.com" */
  destination: string | null;
}

interface SignUpResponse {
  UserConfirmed?: boolean;
  CodeDeliveryDetails?: { Destination?: string };
}

export interface CognitoApi {
  signInWithPassword(email: string, password: string): Promise<CognitoTokens>;
  refreshTokens(refreshToken: string): Promise<CognitoTokens>;
  signUp(email: string, password: string): Promise<SignUpResult>;
  confirmSignUp(email: string, code: string): Promise<void>;
  resendConfirmationCode(email: string): Promise<string | null>;
  forgotPassword(email: string): Promise<string | null>;
  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void>;
  deleteUser(accessToken: string): Promise<void>;
}

export function createCognitoApi(cfg: Pick<CognitoConfig, 'region' | 'clientId'>): CognitoApi {
  const endpoint = `https://cognito-idp.${cfg.region}.amazonaws.com/`;
  const clientId = cfg.clientId;
  const call = <T,>(op: string, body: Record<string, unknown>) => callOp<T>(endpoint, op, body);

  /** Sign in with email + password. Throws `CognitoError` with code
     `UserNotConfirmedException` when the address was never verified — callers
     should send the user to the verification screen rather than show that as a
     failure. */
  async function signInWithPassword(
    email: string,
    password: string,
  ): Promise<CognitoTokens> {
    const res = await call<InitiateAuthResponse>('InitiateAuth', {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: clientId,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    });

    if (!res.AuthenticationResult) {
      // MFA / forced password change — the pool doesn't enable either today, so
      // reaching here means the pool config changed and needs a screen for it.
      throw new CognitoError(
        'UnsupportedChallenge',
        `This account needs an extra step we can’t handle yet${
          res.ChallengeName ? ` (${res.ChallengeName})` : ''
        }.`,
      );
    }
    return toTokens(res.AuthenticationResult);
  }

  /** Exchange a refresh token for fresh id/access tokens. Cognito does not return
     a new refresh token here, so the caller keeps the one it already has. */
  async function refreshTokens(refreshToken: string): Promise<CognitoTokens> {
    const res = await call<InitiateAuthResponse>('InitiateAuth', {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: clientId,
      AuthParameters: { REFRESH_TOKEN: refreshToken },
    });
    if (!res.AuthenticationResult) {
      throw new CognitoError('NotAuthorizedException', 'Session expired.');
    }
    return toTokens(res.AuthenticationResult);
  }


  /** Create an account. The pool has username_attributes = ["email"], so the
     username *is* the email; we still send the `email` attribute so the verified
     address lands on the profile and the id token carries an `email` claim. */
  async function signUp(email: string, password: string): Promise<SignUpResult> {
    const res = await call<SignUpResponse>('SignUp', {
      ClientId: clientId,
      Username: email,
      Password: password,
      UserAttributes: [{ Name: 'email', Value: email }],
    });
    return {
      userConfirmed: res.UserConfirmed ?? false,
      destination: res.CodeDeliveryDetails?.Destination ?? null,
    };
  }

  /** Confirm a new account with the emailed 6-digit code. */
  async function confirmSignUp(email: string, code: string): Promise<void> {
    await call('ConfirmSignUp', {
      ClientId: clientId,
      Username: email,
      ConfirmationCode: code,
    });
  }

  /** Re-send the sign-up verification code. */
  async function resendConfirmationCode(email: string): Promise<string | null> {
    const res = await call<SignUpResponse>('ResendConfirmationCode', {
      ClientId: clientId,
      Username: email,
    });
    return res.CodeDeliveryDetails?.Destination ?? null;
  }

  /** Start a password reset — Cognito emails a code. With
     prevent_user_existence_errors enabled this succeeds even for an unknown
     address, which is what we want: the screen can't be used to probe for
     accounts. */
  async function forgotPassword(email: string): Promise<string | null> {
    const res = await call<SignUpResponse>('ForgotPassword', {
      ClientId: clientId,
      Username: email,
    });
    return res.CodeDeliveryDetails?.Destination ?? null;
  }

  /** Finish a password reset with the emailed code and the new password. */
  async function confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<void> {
    await call('ConfirmForgotPassword', {
      ClientId: clientId,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword,
    });
  }

  /** Delete the signed-in user's own Cognito account, permanently.

     Self-service: authorized by the caller's OWN access token, the same way
     ChangePassword or UpdateUserAttributes would be. That is why this half of
     account deletion lives in the client rather than in the Lambda — not
     reachability (the Lambda has egress), but privilege: AdminDeleteUser would
     let the internet-facing API delete ANY user in the pool, where this can only
     ever delete its own caller. See docs/account-deletion.md. Note this takes the ACCESS token, not
     the id token the API client sends as its bearer.

     Every token the user holds dies with the account, so the caller's only job
     afterwards is to clear its own storage. */
  async function deleteUser(accessToken: string): Promise<void> {
    await call('DeleteUser', { AccessToken: accessToken });
  }

  return {
    signInWithPassword,
    refreshTokens,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    confirmForgotPassword,
    deleteUser,
  };
}

/* ---------- client-side validation ----------
   Mirrors the pool's password_policy in infra/cognito.tf. Keep the two in sync:
   this exists only to fail fast with friendly copy, Cognito is the authority. */

export const PASSWORD_RULES = [
  { label: 'At least 10 characters', test: (p: string) => p.length >= 10 },
  { label: 'A lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'An uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'A number', test: (p: string) => /[0-9]/.test(p) },
] as const;

export function passwordIsValid(password: string): boolean {
  return PASSWORD_RULES.every((r) => r.test(password));
}

/** Deliberately permissive — the server is the real judge, this just catches
   obvious typos before a round trip. */
export function emailIsValid(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
